import { Fragment, useMemo, useState } from "react";
import type { RecommendationView } from "../api/client.js";
import { bookmakerLink } from "./bookmaker-link.js";
import { recommendedBook, type BookAccount } from "./place-bet-dialog.js";
import { TableToolbar } from "./table-toolbar.js";
import { melbourneClock, melbourneWeekday, untilStart } from "../melbourne.js";

type SortKey = "tip" | "book" | "price" | "stake" | "rule";
const SORTS: readonly { readonly id: SortKey; readonly label: string }[] = [
  { id: "tip", label: "Start time" },
  { id: "book", label: "Book" },
  { id: "price", label: "Price, highest" },
  { id: "stake", label: "Stake, largest" },
  { id: "rule", label: "Rule" },
];

/** This book's best Australian price at the bet's own line, if it has one. */
export function priceAtBook(
  recommendation: Pick<RecommendationView, "offers" | "line">,
  book: string,
): number | null {
  const prices = recommendation.offers
    .filter(
      (offer) =>
        offer.bookmaker === book &&
        offer.role === "AU_EXECUTION" &&
        offer.line === recommendation.line &&
        offer.price > 1,
    )
    .map((offer) => offer.price);
  return prices.length === 0 ? null : Math.max(...prices);
}

export function RecommendationsTable({
  recommendations,
  selectedId,
  onSelect,
  onPlace,
  onSkip,
  showSport = false,
  books = [],
}: {
  recommendations: readonly RecommendationView[];
  selectedId: string | null;
  onSelect: (recommendation: RecommendationView) => void;
  onPlace: (recommendation: RecommendationView) => void;
  /** Take an alert that won't be placed off the board. */
  onSkip?: (recommendation: RecommendationView) => void;
  /** Combined board: the row has to say which sport it belongs to. */
  showSport?: boolean;
  /** Account state, so the offer column names the book the router picks
   * rather than whoever is a cent better. */
  books?: readonly BookAccount[];
}) {
  /**
   * Soonest first, with research at the bottom.
   *
   * The board is worked against a clock: the bet that has to be placed next is
   * the one that decides what to do now, and a later game with a better price
   * is not more urgent for being better. Anything that cannot be placed sinks
   * below everything that can - a TEST_ONLY row is a record, not a task, and
   * sitting it among the real ones costs a read every time.
   *
   * A row with no known tip sorts last within its group rather than first: an
   * unknown deadline must never outrank a real one.
   */
  // One book at a time (Moss, 30 Oct 2026): pick a book and the board shows
  // only the bets to place there - ready to log and priced by that book at the
  // bet's line - at its own price, grouped by game, and "Log bet" opens on it.
  const [book, setBook] = useState("ALL");
  const [sortBy, setSortBy] = useState<SortKey>("tip");
  const pickFor = (recommendation: RecommendationView) => {
    if (book === "ALL") return recommendedBook(recommendation, books);
    if (
      recommendation.status !== "RECOMMENDED" ||
      (recommendation.executionMode ?? "LIVE") !== "LIVE"
    )
      return null;
    const price = priceAtBook(recommendation, book);
    return price === null ? null : { bookmaker: book, price };
  };
  const bookOptions = useMemo(() => {
    const names = new Set(
      recommendations.flatMap((recommendation) =>
        recommendation.offers
          .filter((offer) => offer.role === "AU_EXECUTION")
          .map((offer) => offer.bookmaker),
      ),
    );
    return [...names]
      .map((name) => ({
        name,
        count: recommendations.filter(
          (recommendation) =>
            recommendation.status === "RECOMMENDED" &&
            (recommendation.executionMode ?? "LIVE") === "LIVE" &&
            priceAtBook(recommendation, name) !== null,
        ).length,
      }))
      .filter((option) => option.count > 0)
      .sort((left, right) => right.count - left.count);
  }, [recommendations]);
  const byTip = useMemo(
    () =>
      [...recommendations].sort((left, right) => {
        const leftResearch =
          left.executionMode !== undefined && left.executionMode !== "LIVE";
        const rightResearch =
          right.executionMode !== undefined && right.executionMode !== "LIVE";
        if (leftResearch !== rightResearch) return leftResearch ? 1 : -1;
        const leftTip =
          left.tipUtc === undefined ? NaN : Date.parse(left.tipUtc);
        const rightTip =
          right.tipUtc === undefined ? NaN : Date.parse(right.tipUtc);
        const leftKnown = Number.isFinite(leftTip);
        const rightKnown = Number.isFinite(rightTip);
        if (leftKnown !== rightKnown) return leftKnown ? -1 : 1;
        if (!leftKnown) return 0;
        return leftTip - rightTip;
      }),
    [recommendations],
  );
  // Research stays at the bottom whatever the sort; ties keep start order.
  const ordered = byTip
    .flatMap((recommendation) => {
      const pick = pickFor(recommendation);
      return pick === null ? [] : [{ recommendation, pick }];
    })
    .map((row, index) => ({ ...row, index }))
    .sort((left, right) => {
      const research = (row: typeof left) =>
        row.recommendation.executionMode !== undefined &&
        row.recommendation.executionMode !== "LIVE";
      if (research(left) !== research(right)) return research(left) ? 1 : -1;
      // One book: games stay together, in start order (byTip), sorted within.
      if (book !== "ALL") {
        const game = (row: typeof left) =>
          `${row.recommendation.tipUtc ?? ""}|${row.recommendation.gameLabel}`;
        const first = (key: string) =>
          byTip.findIndex(
            (recommendation) =>
              `${recommendation.tipUtc ?? ""}|${recommendation.gameLabel}` ===
              key,
          );
        const gameOrder = first(game(left)) - first(game(right));
        if (gameOrder !== 0) return gameOrder;
      }
      const by =
        sortBy === "book"
          ? left.pick.bookmaker.localeCompare(right.pick.bookmaker)
          : sortBy === "price"
            ? right.pick.price - left.pick.price
            : sortBy === "stake"
              ? right.recommendation.stakeCents - left.recommendation.stakeCents
              : sortBy === "rule"
                ? (
                    left.recommendation.ruleName ??
                    left.recommendation.ruleVersion
                  ).localeCompare(
                    right.recommendation.ruleName ??
                      right.recommendation.ruleVersion,
                  )
                : 0;
      return by !== 0 ? by : left.index - right.index;
    });
  return (
    <>
      <TableToolbar
        book={book}
        books={bookOptions}
        onBook={setBook}
        sort={sortBy}
        sorts={SORTS}
        onSort={setSortBy}
      />
      <div className="table-shell">
        <table className="board-table">
          <thead>
            <tr>
              {showSport ? <th>Sport</th> : null}
              <th>Match</th>
              <th>Locked rule</th>
              <th>Selection / game</th>
              <th>Minimum</th>
              <th>Best AU offer</th>
              <th>Stake</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map(({ recommendation, pick }, position) => (
              <Fragment key={recommendation.signalId}>
                {book !== "ALL" &&
                (position === 0 ||
                  ordered[position - 1]!.recommendation.gameLabel !==
                    recommendation.gameLabel ||
                  ordered[position - 1]!.recommendation.tipUtc !==
                    recommendation.tipUtc) ? (
                  <tr className="game-group">
                    <th colSpan={showSport ? 8 : 7} scope="colgroup">
                      {recommendation.gameLabel}
                      {recommendation.tipUtc === undefined
                        ? ""
                        : ` · ${melbourneClock(recommendation.tipUtc)}`}
                      {" · "}
                      {
                        ordered.filter(
                          (row) =>
                            row.recommendation.gameLabel ===
                              recommendation.gameLabel &&
                            row.recommendation.tipUtc === recommendation.tipUtc,
                        ).length
                      }{" "}
                      to place
                    </th>
                  </tr>
                ) : null}
                <tr
                  className={
                    selectedId === recommendation.signalId ? "selected" : ""
                  }
                  onClick={() => onSelect(recommendation)}
                >
                  {showSport ? (
                    <td className="cell-sport" data-label="Sport">
                      <span className="rule-badge">
                        {recommendation.sport ?? "—"}
                      </span>
                    </td>
                  ) : null}
                  <td className="tabular cell-when" data-label="Match">
                    {/* The day matters as much as the time: an NFL total is
                    priced four days out, and "10:15 am" alone does not say
                    which morning. */}
                    {recommendation.tipUtc === undefined ? (
                      recommendation.melbourneTip
                    ) : (
                      <>
                        <strong>
                          {melbourneWeekday(recommendation.tipUtc)}
                        </strong>
                        <small>
                          {melbourneClock(recommendation.tipUtc)} ·{" "}
                          {untilStart(recommendation.tipUtc)}
                        </small>
                      </>
                    )}
                  </td>
                  <td className="cell-rule" data-label="Locked rule">
                    <span
                      className="rule-badge"
                      title={recommendation.ruleVersion}
                    >
                      {recommendation.ruleName ?? recommendation.ruleVersion}
                    </span>
                  </td>
                  <td className="cell-bet" data-label="Selection / game">
                    <strong>{recommendation.description}</strong>
                    <small>{recommendation.gameLabel}</small>
                  </td>
                  <td className="tabular cell-min" data-label="Minimum">
                    <strong>
                      {recommendation.executionMode === "TRACK_ONLY"
                        ? "N/A"
                        : recommendation.minimumOdds > 0
                          ? recommendation.minimumOdds.toFixed(2)
                          : "—"}
                    </strong>
                    {recommendation.executionMode !== "TRACK_ONLY" &&
                    recommendation.minimumOdds <= 0 ? (
                      // Under two books quoting both sides there is no fair price
                      // to set a minimum from.
                      <small>No fair price yet</small>
                    ) : null}
                  </td>
                  <td className="cell-offer" data-label="Best AU offer">
                    {(() => {
                      // The router's book and its price lead; the best price is
                      // named underneath only when it is at another book.
                      const href = bookmakerLink(
                        pick.bookmaker,
                        recommendation.offers,
                      );
                      return (
                        <>
                          <strong
                            className={
                              recommendation.minimumOdds <= 0
                                ? undefined
                                : recommendation.executionMode ===
                                      "TRACK_ONLY" ||
                                    pick.price >= recommendation.minimumOdds
                                  ? "positive"
                                  : "negative"
                            }
                          >
                            {pick.price.toFixed(2)}
                          </strong>
                          <small>
                            {href === null ? (
                              pick.bookmaker
                            ) : (
                              <a
                                className="book-link"
                                href={href}
                                target="_blank"
                                rel="noreferrer noopener"
                                title={`Open ${pick.bookmaker}`}
                                onClick={(event) => event.stopPropagation()}
                              >
                                {pick.bookmaker} ↗
                              </a>
                            )}
                            {pick.bookmaker === recommendation.bookmaker
                              ? ""
                              : ` · best ${recommendation.price.toFixed(2)} at ${recommendation.bookmaker}`}
                            {recommendation.priceIsStale === true
                              ? " · price as recommended, check at the book"
                              : ""}
                          </small>
                        </>
                      );
                    })()}
                  </td>
                  <td className="cell-stake" data-label="Stake">
                    <strong>{recommendation.units}u</strong>
                    <small>
                      A${(recommendation.stakeCents / 100).toFixed(2)}
                    </small>
                  </td>
                  <td className="cell-action" data-label="Action">
                    <button
                      type="button"
                      className="button primary compact"
                      disabled={
                        recommendation.status !== "RECOMMENDED" ||
                        (recommendation.executionMode ?? "LIVE") !== "LIVE"
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        // Filtered to one book: the dialog opens on it, at its price.
                        onPlace(
                          book === "ALL"
                            ? recommendation
                            : {
                                ...recommendation,
                                bookmaker: pick.bookmaker,
                                price: pick.price,
                              },
                        );
                      }}
                    >
                      {(recommendation.executionMode ?? "LIVE") !== "LIVE"
                        ? recommendation.executionMode === "TEST_ONLY"
                          ? "Test only"
                          : "Track only"
                        : recommendation.status === "RECOMMENDED"
                          ? "Log bet"
                          : recommendation.status === "BELOW_VALUE"
                            ? "Pass"
                            : "Lineup gate"}
                    </button>
                    {onSkip !== undefined &&
                    recommendation.status === "RECOMMENDED" &&
                    (recommendation.executionMode ?? "LIVE") === "LIVE" ? (
                      <button
                        type="button"
                        className="button compact"
                        onClick={(event) => {
                          event.stopPropagation();
                          onSkip(recommendation);
                        }}
                      >
                        Skip
                      </button>
                    ) : null}
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
